import { useCallback, useEffect, useRef, useState } from 'react'
import simplify from '@turf/simplify'
import turfArea from '@turf/area'
import turfCentroid from '@turf/centroid'
import {
  MdClose,
  MdOutlineCheck,
  MdOutlineDelete,
  MdOutlineEdit,
} from 'react-icons/md'
import {
  useFilterStore,
  useMapStore,
  usePointStore,
  usePolygonStore,
} from '../../../../state-library/src'
import { ESearchLevel } from '../../../../types-library/src'
import { getSearchLevelByZoom } from '../../utils'
import { useWindowSize } from '@wiggot/hooks/src/useWindowsSize'
import { ILocation } from '../../../../state-library/src/stores/filterStore/types'


// import { useBoundStore } from '@/src/store'
// import { toast, useAmplitude } from '@mo/web-components'
// import { AnimatePresence, motion } from 'framer-motion'
// import { useWindowSize } from '@mo/hooks'
// import { EAmplitudeEvent, ESearchLevel } from '@/src/interfaces'
// import { getSearchLevelByZoom } from '@/src/utils/listingsTiles'
// import { getUserDataFromLocalStorage, isAuthenticated } from '@mo/auth'
// import {
//   MdClose,
//   MdOutlineEdit,
//   MdOutlineDelete,
//   MdOutlineCheck
// } from 'react-icons/md'
// import FeatureFlag from '@/src/components/featureFlag'
// import { useHistogramStore } from '../../store/useHistogramStore'
// import { ELoaderVariant } from '@/src/interfaces/store/loader-grid-interface'
// import { composeClasses } from '@mo/utils'

interface Line {
  type: 'Feature'
  properties: object
  geometry: {
    type: 'LineString'
    coordinates: number[][]
  }
}

const base64Image =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzYiIGhlaWdodD0iMzYiIHZpZXdCb3g9IjAgMCAzNiAzNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgaWQ9Im1vZGVfZWRpdCI+CjxnIGlkPSJWZWN0b3IiPgo8cGF0aCBkPSJNNC40OTgwNSAzMS41MDE5SDEwLjEyM0wyNi43MTMgMTQuOTExOUwyMS4wODggOS4yODY5Mkw0LjQ5ODA1IDI1Ljg3NjlWMzEuNTAxOVpNNy40OTgwNSAyNy4xMjE5TDIxLjA4OCAxMy41MzE5TDIyLjQ2OCAxNC45MTE5TDguODc4MDUgMjguNTAxOUg3LjQ5ODA1VjI3LjEyMTlaIiBmaWxsPSJibGFjayIvPgo8cGF0aCBkPSJNMjcuNTUzIDQuOTM2OTJDMjYuOTY4IDQuMzUxOTIgMjYuMDIzIDQuMzUxOTIgMjUuNDM4IDQuOTM2OTJMMjIuNjkzIDcuNjgxOTJMMjguMzE4IDEzLjMwNjlMMzEuMDYzIDEwLjU2MTlDMzEuNjQ4IDkuOTc2OTIgMzEuNjQ4IDkuMDMxOTIgMzEuMDYzIDguNDQ2OTJMMjcuNTUzIDQuOTM2OTJaIiBmaWxsPSJibGFjayIvPgo8L2c+CjwvZz4KPC9zdmc+Cg=='

const DrawingTool: React.FC = () => {
  const size = useWindowSize()

  // const { trackEvent } = useAmplitude()

  // const {
  //   map,
  //   isLoadedMap,
  //   listingsSvgContainer,
  //   drawingTool,
  //   latitude,
  //   longitude,
  //   searchLevel,
  //   pamRequestId,
  //   isEnabledHeatMap,

  //   setAreaBounds,
  //   setDrawingTool,
  //   setLatitude,
  //   setLongitude,
  //   setSearchLevel,
  //   setLoaderVariant,
  // } = useBoundStore((state) => state)

  // const { setIsLoadingHistogram } = useHistogramStore()

  const {
    latitude,
    longitude,
    searchLevel,
    locations,
    setAreaBounds,
    setLatitude,
    setLongitude,
    setSearchLevel,
    setLocations
  } = useFilterStore()

  const { map, isLoadedMap, drawingTool, setDrawingTool } = useMapStore()

  const { pointsSvgContainer } = usePointStore()

  const { isEnabledHeatMap } = usePolygonStore()

  const drawStatus = useRef<typeof drawingTool>(drawingTool)
  drawStatus.current = drawingTool

  const [line, setLine] = useState<Line>({
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: [],
    },
  })

  const lineRef = useRef<Line>(line)
  lineRef.current = line

  const [isViewInitToast, setIsViewInitToast] = useState<boolean>(false)

  const [geoBackup, setGeoBackup] = useState<
    | {
        latitude: number
        longitude: number
        searchLevel: ESearchLevel
        locations: ILocation[]
      }
    | undefined
  >()

  const onMouseDown = useCallback(
    (event: any) => {
      if (
        !map ||
        (drawStatus.current.status !== 'waiting' &&
          drawStatus.current.status !== 'figure')
      ) {
        return
      }

      event.preventDefault()

      setDrawingTool({
        isActive: true,
        status: 'drawing',
      })

      setLine({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [event.lngLat.toArray()],
        },
      })
    },
    [map]
  )

  const onMouseMove = useCallback(
    (event: any) => {
      if (!map || drawStatus.current.status !== 'drawing') {
        return
      }
      setLine((currentLine) => ({
        ...currentLine,
        geometry: {
          ...currentLine.geometry,
          coordinates: [
            ...currentLine.geometry.coordinates,
            event.lngLat.toArray(),
          ],
        },
      }))
    },
    [map]
  )

  const onMouseUp = useCallback(() => {
    if (!map || drawStatus.current.status !== 'drawing') {
      return
    }

    const allCoordinates = lineRef.current.geometry.coordinates
    const first = allCoordinates[0]

    setLine((currentLine) => ({
      ...currentLine,
      geometry: {
        ...currentLine.geometry,
        coordinates: [...currentLine.geometry.coordinates, first],
      },
    }))

    setDrawingTool({
      isActive: true,
      status: 'figure',
    })
  }, [map])

  const getTolerance = (params: { zoom: number }) => {
    const { zoom } = params

    let tolerance = 0.002

    if (zoom > 10.5) {
      tolerance = 0.001
    }

    if (zoom > 11.5) {
      tolerance = 0.0008
    }

    if (zoom > 12.5) {
      tolerance = 0.0003
    }

    if (zoom > 14.5) {
      tolerance = 0.0001
    }

    return tolerance
  }

  const loadDrawBounds = () => {
    try {
      if (lineRef.current.geometry.coordinates.length > 0) {
        // trackDrawTool({ event: EAmplitudeEvent.DRAWZONE_SELECTED })

        setDrawingTool({
          isActive: true,
          status: 'processing',
        })

        const zoom = map?.getZoom() || 0

        const simplifiedLine = simplify(lineRef.current, {
          tolerance: getTolerance({ zoom }),
          highQuality: true,
        })

        const bounds = simplifiedLine.geometry.coordinates

        const area = turfArea({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [bounds],
          },
        })

        if (!map) {
          return
        }

        if (area < 10000) {
          throw new Error()
        } else {
          if (!geoBackup) {
            setGeoBackup({
              latitude,
              longitude,
              searchLevel,
              locations,
            })
          }

          pointsSvgContainer?.style('cursor', 'grab')
          map.getCanvas().style.cursor = 'grab'

          const centroid = turfCentroid(simplifiedLine)

          const level = getSearchLevelByZoom({ zoom })

          // setLoaderVariant(ELoaderVariant.FROM_SEARCH_AND_ZONE)
          // setIsLoadingHistogram(true)
          setAreaBounds(bounds)
          setLatitude(centroid.geometry.coordinates[1])
          setLongitude(centroid.geometry.coordinates[0])
          setLocations([])
          setSearchLevel(level)

          map.flyTo({
            center: {
              lat: centroid.geometry.coordinates[1],
              lng: centroid.geometry.coordinates[0],
            },
            zoom: map.getZoom(),
            duration: 500,
            essential: true,
          })

          const drawLayer = map.getLayer('drawing')
          if (drawLayer) {
            map.setPaintProperty('drawing', 'line-dasharray', [2, 2])
          }

          setTimeout(() => {
            if (drawLayer) {
              map.setPaintProperty('drawing', 'line-dasharray', undefined)
            }

            setDrawingTool({
              isActive: true,
              status: 'processed',
            })

            setLine((currentLine) => ({
              ...currentLine,
              geometry: {
                ...currentLine.geometry,
                coordinates: bounds,
              },
            }))

            map.dragPan.enable()
          }, 1000)
        }
      }
    } catch (error) {
      setDrawingTool({
        isActive: true,
        status: 'waiting',
      })

      // toast({
      //   type: 'warning',
      //   title: 'El área de la zona es muy pequeña',
      //   position: size?.width < 768 ? 'bottom-center' : 'top-right'
      // })
    }
  }

  const activeDrawing = () => {
    if (!map) {
      return
    }

    if (!isViewInitToast && size.width > 768) {
      setIsViewInitToast(true)
      // toast({
      //   type: 'info',
      //   title: 'Dibuja tu zona de interés',
      //   position: size?.width < 768 ? 'bottom-center' : 'top-right'
      // })
    }

    setDrawingTool({
      isActive: true,
      status: 'waiting',
    })
    pointsSvgContainer?.style('cursor', `url('${base64Image}') 5 32, auto`)
    map.getCanvas().style.cursor = `url('${base64Image}') 5 32, auto`

    if (size.width < 768) {
      map.on('touchstart', onMouseDown)
      map.on('touchmove', onMouseMove)
      map.on('touchend', onMouseUp)
    } else {
      map.on('mousedown', onMouseDown)
      map.on('mousemove', onMouseMove)
      map.on('mouseup', onMouseUp)
    }

    map.dragPan.disable()

    // trackDrawTool({ event: EAmplitudeEvent.DRAWZONE_STARTED })
  }

  // const trackDrawTool = (params: { event: EAmplitudeEvent }) => {
  //   const { event } = params
  //   let userId = ''

  //   if (isAuthenticated()) {
  //     const { email, auth0UserId } = getUserDataFromLocalStorage()
  //     userId = email || auth0UserId
  //   }

  //   trackEvent({
  //     name: event,
  //     eventProperties: {
  //       device: size.width < 768 ? 'mobile' : 'desktop',
  //       userId
  //     }
  //   })
  // }

  const closeDrawing = () => {
    if (!map) {
      return
    }

    clearDraw()

    if (geoBackup) {
      const { latitude: lat, longitude: lng, searchLevel: level, locations } = geoBackup

      // setIsLoadingHistogram(true)
      setSearchLevel(level)
      setLatitude(lat)
      setLongitude(lng)
      setGeoBackup(undefined)
      setLocations(locations)
      setAreaBounds([])
    }

    setDrawingTool({
      isActive: false,
      status: undefined,
    })

    if (size.width < 768) {
      map.off('touchstart', onMouseDown)
      map.off('touchmove', onMouseMove)
      map.off('touchend', onMouseUp)
    } else {
      map.off('mousedown', onMouseDown)
      map.off('mousemove', onMouseMove)
      map.off('mouseup', onMouseUp)
    }

    map.dragPan.enable()
    pointsSvgContainer?.style('cursor', 'grab')
    map.getCanvas().style.cursor = 'grab'
  }

  const externalCloseDrawing = () => {
    if (!map) {
      return
    }

    if (size.width < 768) {
      map.off('touchstart', onMouseDown)
      map.off('touchmove', onMouseMove)
      map.off('touchend', onMouseUp)
    } else {
      map.off('mousedown', onMouseDown)
      map.off('mousemove', onMouseMove)
      map.off('mouseup', onMouseUp)
    }

    setLine({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [],
      },
    })

    const source = map?.getSource('drawing') as mapboxgl.GeoJSONSource

    if (source) {
      source?.setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [],
        },
      })
    }

    map?.dragPan?.enable()
    pointsSvgContainer?.style?.('cursor', 'grab')
    map.getCanvas().style.cursor = 'grab'
  }

  const clearDraw = () => {
    if (!map) {
      return
    }

    setDrawingTool({
      isActive: true,
      status: 'waiting',
    })

    map?.dragPan?.disable?.()

    pointsSvgContainer?.style?.('cursor', `url('${base64Image}') 5 32, auto`)
    map.getCanvas().style.cursor = `url('${base64Image}') 5 32, auto`

    setLine({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [],
      },
    })

    const source = map?.getSource('drawing') as mapboxgl.GeoJSONSource

    if (source) {
      source?.setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [],
        },
      })
    }
  }

  useEffect(() => {
    if (map && isLoadedMap) {
      if (!map?.getSource('drawing')) {
        map?.addSource('drawing', {
          type: 'geojson',
          data: line,
        })

        map?.addLayer({
          id: 'drawing',
          type: 'line',
          source: 'drawing',
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#0368FE',
            'line-width': 2,
          },
        })
      }
    }
    return () => {
      if (map?.getLayer('drawing')) {
        map?.removeLayer('drawing')
        map?.removeSource('drawing')
      }
    }
  }, [map, isLoadedMap])

  useEffect(() => {
    if (map && line.geometry.coordinates.length > 0) {
      const source = map.getSource('drawing') as mapboxgl.GeoJSONSource

      if (source) {
        source.setData(line)
      }
    }
  }, [line, map])

  useEffect(() => {
    if (!drawingTool.isActive && !drawingTool.status && map) {
      externalCloseDrawing()
      setGeoBackup(undefined)
    }
  }, [drawingTool])

  useEffect(() => {
    const onScroll = (event: any) => {
      if (
        drawingTool.status === 'waiting' ||
        drawingTool.status === 'drawing'
      ) {
        event.preventDefault()
      }
    }

    window.addEventListener('touchmove', onScroll, { passive: false })

    return () => {
      window.removeEventListener('touchmove', onScroll)
    }
  }, [drawingTool?.status])

  return (
    <>
      <div
      // classNameInDisabled="opacity-50"
      // isDisabled={Boolean(pamRequestId)}
      // hiddenInDisabled={false}
      >
        {/* <AnimatePresence> */}
        {drawingTool.isActive ? (
          <div className="flex flex-col gap-3">
            {/* Close */}
            <button
              onClick={closeDrawing}
              disabled={drawingTool.status === 'processing'}
              type="button"
              className="w-[42px] h-[42px] flex items-center justify-center bg-white rounded-full shadow-elevation-3 active:scale-95 duration-200"
            >
              <MdClose className="fill-primary w-6 h-6" />
            </button>

            {/* Clear */}
            <button
              onClick={clearDraw}
              disabled={drawingTool.status === 'processing'}
              // initial={{ opacity: 0, width: 0, height: 0 }}
              // animate={{
              //   opacity: 1,
              //   width: 42,
              //   height: 42,
              //   transition: { duration: 0.01 }
              // }}
              // exit={{
              //   opacity: 0,
              //   width: 0,
              //   height: 0,
              //   transition: {
              //     duration: 0.15,
              //     type: 'tween',
              //     ease: 'easeOut'
              //   }
              // }}
              type="button"
              className="w-[42px] h-[42px] flex items-center justify-center bg-white rounded-full shadow-elevation-3 active:scale-95 duration-200"
            >
              <MdOutlineDelete className="fill-primary w-6 h-6" />
            </button>

            {/* Ok */}
            {/* <AnimatePresence> */}
            {drawingTool.status === 'figure' ||
            drawingTool.status === 'processing' ? (
              <button
                type="button"
                // initial={{ opacity: 0, width: 0, height: 0 }}
                // animate={{
                //   opacity: 1,
                //   width: 42,
                //   height: 42,
                //   transition: { duration: 0.01 }
                // }}
                // exit={{
                //   opacity: 0,
                //   width: 0,
                //   height: 0,
                //   transition: {
                //     duration: 0.15,
                //     type: 'tween',
                //     ease: 'easeOut'
                //   }
                // }}
                disabled={drawingTool.status === 'processing'}
                onClick={loadDrawBounds}
                className="w-[42px] h-[42px] flex items-center justify-center bg-white rounded-full shadow-elevation-3 active:scale-95 duration-200"
              >
                {drawingTool.status === 'processing' ? (
                  <div className="w-5 h-5 animate-spin border-2 border-l-blue  border-t-blue  border-r-white border-b-white rounded-full" />
                ) : (
                  <MdOutlineCheck className="fill-notif-green w-6 h-6" />
                )}
              </button>
            ) : null}
            {/* </AnimatePresence> */}
          </div>
        ) : (
          <button
            type="button"
            onClick={activeDrawing}
            // initial={{ opacity: 0, y: 20 }}
            // animate={{
            //   opacity: 1,
            //   y: 0,
            //   transition: { duration: 0.01 }
            // }}
            // exit={{
            //   opacity: 0,
            //   y: 20,
            //   transition: {
            //     duration: 0.15,
            //     type: 'tween',
            //     ease: 'easeOut'
            //   }
            // }}
            className={[
              'w-[42px] h-[42px] md:w-auto md:h-auto flex items-center justify-center gap-1 md:px-3.5 md:py-2 bg-white rounded-full shadow-elevation-3 active:scale-95 duration-200',
              isEnabledHeatMap ? 'md:!w-9 md:!h-9 !p-0' : '',
            ].join(' ')}
          >
            {isEnabledHeatMap ? null : (
              <p className="text-body-bold-m text-primary hidden md:block">
                Dibujar zona
              </p>
            )}

            <MdOutlineEdit
              className={[
                'text-primary md:w-5 md:h-5 w-6 h-6',
                isEnabledHeatMap ? 'md:!w-5 md:!h-5' : '',
              ].join(' ')}
            />
          </button>
        )}
        {/* </AnimatePresence> */}
      </div>
    </>
  )
}

export default DrawingTool
